import type { ReactNode, JSX } from 'react';
import { Navigate } from 'react-router-dom';

import { useTenantFeatures } from '../../hooks/useTenantFeatures';
import { TenantFeature, TenantType } from '../../types/tenant.types';

interface FeatureGuardedRouteProps {
  /**
   * The feature required to access this route
   */
  feature?: TenantFeature;

  /**
   * Multiple features - all must be present (AND logic)
   */
  features?: TenantFeature[];

  /**
   * Any of these features - at least one must be present (OR logic)
   */
  anyFeature?: TenantFeature[];

  /**
   * Specific tenant type required
   */
  tenantType?: TenantType;

  /**
   * Any of these tenant types
   */
  anyTenantType?: TenantType[];

  /**
   * Content to render when feature is available
   */
  children: ReactNode;

  /**
   * Redirect path when access is denied
   * Defaults to /dashboard
   */
  redirectTo?: string;
}

/**
 * FeatureGuardedRoute Component
 *
 * Protects routes based on tenant features and/or type.
 * Redirects to another route if access is denied.
 *
 * Usage:
 * ```tsx
 * <Route
 *   path="/incubators"
 *   element={
 *     <FeatureGuardedRoute feature={TenantFeature.INCUBATORS}>
 *       <IncubatorsPage />
 *     </FeatureGuardedRoute>
 *   }
 * />
 * ```
 */
export function FeatureGuardedRoute({
  feature,
  features,
  anyFeature,
  tenantType,
  anyTenantType,
  children,
  redirectTo = '/dashboard',
}: FeatureGuardedRouteProps): JSX.Element {
  const { hasFeature, tenantType: currentType } = useTenantFeatures();

  // Check single feature
  if (feature && !hasFeature(feature)) {
    return <Navigate to={redirectTo} replace />;
  }

  // Check multiple features (AND logic)
  if (features && features.length > 0) {
    const hasAllFeatures = features.every((f) => hasFeature(f));
    if (!hasAllFeatures) {
      return <Navigate to={redirectTo} replace />;
    }
  }

  // Check any feature (OR logic)
  if (anyFeature && anyFeature.length > 0) {
    const hasAnyFeature = anyFeature.some((f) => hasFeature(f));
    if (!hasAnyFeature) {
      return <Navigate to={redirectTo} replace />;
    }
  }

  // Check specific tenant type
  if (tenantType && currentType !== tenantType) {
    return <Navigate to={redirectTo} replace />;
  }

  // Check any tenant type
  if (anyTenantType && anyTenantType.length > 0) {
    if (!anyTenantType.includes(currentType as TenantType)) {
      return <Navigate to={redirectTo} replace />;
    }
  }

  return <>{children}</>;
}

export default FeatureGuardedRoute;

